import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search, Sparkles, Users, BookMarked, ChevronDown, ArrowRight,
  PenLine, ShieldCheck, Star, Zap, BookOpen, PenSquare, Tag, Check, LayoutGrid,
} from 'lucide-react';
import { formatCompactNumber } from '../../utils/format';
import { api } from '../../lib/api';
import { normalizeBooks } from '../../lib/normalizers';
import { buildSuggestions } from '../../lib/searchSuggestions';
import { useLiveReaderCount } from '../../hooks/useLiveReaderCount';
import { useGenres } from '../../hooks/useGenres';
import { useTrendingSearches } from '../../hooks/useTrendingSearches';
import AuthorInfoModal from '../account/AuthorInfoModal';

const SUGGESTION_ICONS = { book: BookOpen, author: PenSquare, genre: Tag };

const statConfig = [
  { key: 'titles', icon: BookMarked, label: 'titles' },
  { key: 'authors', icon: PenLine, label: 'authors' },
  { key: 'readers', icon: Users, label: 'readers' },
];

const HERO_IMAGES = [
  { src: '/hero1.png', alt: 'A reader using a tablet to read an e-book' },
  { src: '/hero2.png', alt: 'A reader enjoying an e-book on a cozy couch' },
  { src: '/hero3.png', alt: 'A reader browsing e-books on a laptop' },
];

const HERO_SLIDE_INTERVAL = 7000;

export default function Hero() {
  const [query, setQuery] = useState('');
  const [genre, setGenre] = useState('');
  const [homeStats, setHomeStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [checkingSearch, setCheckingSearch] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [authorInfoOpen, setAuthorInfoOpen] = useState(false);
  // Free tier's live rate — a visitor clicking "Become an author" from the
  // homepage hasn't signed in yet, so they'd start on the free tier (see
  // Publish.jsx, which uses the same fallback for a signed-out author).
  const [royaltyPercent, setRoyaltyPercent] = useState(70);
  const navigate = useNavigate();
  const liveReaders = useLiveReaderCount();
  const genres = useGenres({ withBooks: true });
  const trendingTags = useTrendingSearches();
  const categoryRef = useRef(null);
  const searchErrorTimerRef = useRef(null);
  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((i) => (i + 1) % HERO_IMAGES.length);
    }, HERO_SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!categoryOpen) return;
    const onClickOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setCategoryOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [categoryOpen]);

  useEffect(() => {
    let cancelled = false;
    api.get('/stats/home')
      .then((data) => { if (!cancelled) setHomeStats(data); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/membership/plans')
      .then((data) => {
        if (!cancelled && typeof data.plans?.free?.royaltyPercent === 'number') {
          setRoyaltyPercent(data.plans.free.royaltyPercent);
        }
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const data = await api.get(`/books/suggest?q=${encodeURIComponent(q)}&limit=8`, { signal: controller.signal });
        if (cancelled) return;
        setSuggestions(buildSuggestions(q, normalizeBooks(data.books), genres));
        setSuggestionsOpen(true);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setSuggestionsOpen(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, genres]);

  const clearSearchErrorTimer = () => {
    if (searchErrorTimerRef.current) {
      clearTimeout(searchErrorTimerRef.current);
      searchErrorTimerRef.current = null;
    }
  };

  const showSearchError = (message) => {
    clearSearchErrorTimer();
    setSearchError(message);
    searchErrorTimerRef.current = setTimeout(() => {
      setSearchError('');
      searchErrorTimerRef.current = null;
    }, 2000);
  };

  const dismissSearchError = () => {
    clearSearchErrorTimer();
    setSearchError('');
  };

  useEffect(() => clearSearchErrorTimer, []);

  const goToSuggestion = (suggestion) => {
    navigate(suggestion.to);
    setQuery('');
    setSuggestions([]);
    setSuggestionsOpen(false);
    dismissSearchError();
  };

  const stats = statConfig.map(({ key, icon, label }) => ({
    icon,
    label,
    value: homeStats ? formatCompactNumber(homeStats[key]) : '—',
  }));

  const trustBadges = [
    { icon: ShieldCheck, label: 'Secure checkout' },
    {
      icon: Star,
      label: homeStats?.avgRating ? `${homeStats.avgRating}/5 reader rating` : 'Real reader ratings',
    },
    { icon: Zap, label: 'Instant, on any device' },
  ];

  // Checks for at least one match before leaving the homepage — a search
  // with zero results shows an inline error here instead of landing the
  // user on an empty /shop page. An empty query+genre is just "browse all",
  // not a search, so that still navigates straight through.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (checkingSearch) return;

    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (genre) params.set('genre', genre);

    if (!query && !genre) {
      navigate('/shop');
      setSuggestionsOpen(false);
      return;
    }

    dismissSearchError();
    setSuggestionsOpen(false);
    setCheckingSearch(true);
    try {
      const data = await api.get(`/books?${params.toString()}&limit=1`);
      if (!data.pagination?.total) {
        showSearchError(
          `No results found${query ? ` for "${query}"` : ''}${genre ? ` in ${genre}` : ''}.`
        );
        return;
      }
      navigate(`/shop?${params.toString()}`);
    } catch {
      showSearchError('Something went wrong. Please try again.');
    } finally {
      setCheckingSearch(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-mesh dark:bg-[linear-gradient(135deg,#0f0e17_0%,#1a1932_55%,#0f0e17_100%)]">
      {/* Ambient glow accents */}
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-primary-400/30 blur-[110px] dark:bg-primary-600/25" />
      <div className="pointer-events-none absolute top-1/3 -right-28 h-[26rem] w-[26rem] rounded-full bg-accent-400/25 blur-[110px] dark:bg-accent-500/15" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-radial-glow opacity-0 dark:opacity-100" />

      <div className="container-app relative grid gap-14 py-16 sm:py-20 lg:grid-cols-2 lg:py-28 lg:items-center">
        {/* Left: copy + search */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="eyebrow inline-flex w-fit items-center gap-2 rounded-full border border-primary-200/60 dark:border-white/10 bg-white/60 dark:bg-white/5 px-3 py-1.5 backdrop-blur"
          >
            <Sparkles size={14} /> AI-powered reading, reimagined
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] text-slate-900 dark:text-white"
          >
            Your next
            <span className="text-gradient italic sm:whitespace-nowrap"> favorite e-book</span>
            <br /> is already open.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 max-w-lg text-base sm:text-lg text-slate-600 dark:text-slate-300"
          >
            Thousands of ebooks from bestselling and independent
            authors — read instantly on any device, no waiting for shipping.
          </motion.p>

          {/* Search bar with category filter */}
          <motion.form
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            onSubmit={onSubmit}
            className="relative mt-8 max-w-xl"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-2xl sm:rounded-full border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-xl p-2 shadow-soft-lg">
              <div className="flex flex-1 items-center gap-2 pl-3 pr-1 py-1.5">
                <Search size={18} className="shrink-0 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); dismissSearchError(); }}
                  onFocus={() => suggestions.length > 0 && setSuggestionsOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                  placeholder="Search by title, author, or genre..."
                  className="w-full bg-transparent text-sm placeholder:text-slate-400 focus:outline-none focus:ring-0 focus-visible:outline-transparent"
                />
              </div>

              <div
                ref={categoryRef}
                className="relative flex items-center border-t sm:border-t-0 sm:border-l border-slate-200 dark:border-white/10 px-1 py-2 sm:py-1.5"
              >
                <button
                  type="button"
                  onClick={() => setCategoryOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={categoryOpen}
                  className="flex items-center gap-1.5 rounded-[10px] px-3 py-1 text-sm text-slate-600 dark:text-slate-300 outline-none transition-colors hover:text-primary-600 dark:hover:text-primary-300"
                >
                  <LayoutGrid size={14} className="shrink-0 text-slate-400" />
                  <span className="max-w-[7rem] truncate">{genre || 'All categories'}</span>
                  <ChevronDown
                    size={14}
                    className={`shrink-0 text-slate-400 transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {categoryOpen && (
                    <motion.ul
                      role="listbox"
                      initial={{ opacity: 0, y: 6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-[calc(100%+0.5rem)] z-40 max-h-72 w-56 overflow-auto rounded-xl2 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard p-1.5 shadow-soft-lg"
                    >
                      <li>
                        <button
                          type="button"
                          role="option"
                          aria-selected={genre === ''}
                          onClick={() => { setGenre(''); setCategoryOpen(false); dismissSearchError(); }}
                          className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-600 dark:text-slate-300 transition-colors hover:bg-primary-50 dark:hover:bg-white/5"
                        >
                          All categories
                          {genre === '' && <Check size={14} className="shrink-0 text-primary-600 dark:text-primary-300" />}
                        </button>
                      </li>
                      {genres.map((g) => (
                        <li key={g}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={genre === g}
                            onClick={() => { setGenre(g); setCategoryOpen(false); dismissSearchError(); }}
                            className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-600 dark:text-slate-300 transition-colors hover:bg-primary-50 dark:hover:bg-white/5"
                          >
                            <span className="truncate">{g}</span>
                            {genre === g && <Check size={14} className="shrink-0 text-primary-600 dark:text-primary-300" />}
                          </button>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
              <button type="submit" disabled={checkingSearch} className="btn-primary !px-6 !py-3 shrink-0 text-sm disabled:opacity-60">
                {checkingSearch ? 'Searching…' : 'Search'}
              </button>
            </div>

            <AnimatePresence>
              {searchError && (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full z-40 mt-2 w-fit max-w-full rounded-xl2 border border-red-200 dark:border-red-900/40 bg-white dark:bg-surface-darkCard px-4 py-2.5 text-xs text-red-500 shadow-soft-lg"
                >
                  {searchError}
                </motion.p>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {suggestionsOpen && suggestions.length > 0 && (
                <motion.ul
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-xl2 border border-slate-100 dark:border-white/10 bg-white dark:bg-surface-darkCard shadow-soft-lg"
                >
                  {suggestions.map((s) => {
                    const Icon = SUGGESTION_ICONS[s.type];
                    return (
                      <li key={`${s.type}-${s.label}`}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => goToSuggestion(s)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-primary-50 dark:hover:bg-white/5"
                        >
                          <Icon size={15} className="shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{s.label}</span>
                          {s.sub && (
                            <span className="shrink-0 text-xs text-slate-400">{s.sub}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </motion.ul>
              )}
            </AnimatePresence>

            {/* Recent Searches */}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-400 dark:text-slate-500">Recent Searches:</span>
              {trendingTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/shop?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-slate-200 dark:border-white/10 bg-white/70 dark:bg-white/5 px-3 py-1 text-slate-600 dark:text-slate-300 transition-colors hover:border-primary-300 hover:text-primary-600 dark:hover:border-primary-400/40 dark:hover:text-primary-300"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </motion.form>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 flex flex-nowrap items-center justify-center gap-2 sm:flex-wrap sm:justify-start sm:gap-3"
          >
            <Link to="/shop" className="btn-primary !px-4 !py-2 text-xs whitespace-nowrap sm:!px-6 sm:!py-3 sm:text-sm">
              Explore E-books <ArrowRight size={14} className="shrink-0" />
            </Link>
            <button type="button" onClick={() => setAuthorInfoOpen(true)} className="btn-secondary !px-4 !py-2 text-xs whitespace-nowrap sm:!px-6 sm:!py-3 sm:text-sm">
              <PenLine size={14} className="shrink-0" /> Become an Author
            </button>
          </motion.div>

          {/* Reader stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="mt-10 flex flex-wrap items-center gap-8"
          >
            {stats.map(({ icon: Icon, value, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon size={18} className="text-primary-600 dark:text-primary-300" />
                <span className="text-sm font-semibold text-slate-800 dark:text-white">{value}</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
              </div>
            ))}
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.34 }}
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200/70 dark:border-white/10 pt-6"
          >
            {trustBadges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                <Icon size={14} className="text-primary-500 dark:text-primary-300" />
                {label}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: hero photo, framed with a gradient border matching the brand colors */}
        <div className="relative hidden lg:block lg:self-start lg:pt-4">
          {/* Soft glow behind the photo, echoing the gradient border */}
          <div className="pointer-events-none absolute inset-x-4 top-4 bottom-8 rounded-xl3 bg-gradient-primary opacity-25 blur-3xl" />

          {/* Reader photo — reading an e-book on a tablet */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative z-20 animate-floatSlow"
          >
            <div className="relative rounded-xl3 bg-gradient-primary p-[3px] shadow-soft-lg">
              <div className="relative aspect-[8/5] w-full overflow-hidden rounded-[calc(1.25rem-3px)]">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={heroSlide}
                    src={HERO_IMAGES[heroSlide].src}
                    alt={HERO_IMAGES[heroSlide].alt}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: 'easeInOut' }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
              </div>

              {/* Slide indicators */}
              <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1.5">
                {HERO_IMAGES.map((img, i) => (
                  <button
                    key={img.src}
                    type="button"
                    onClick={() => setHeroSlide(i)}
                    aria-label={`Show slide ${i + 1}`}
                    aria-current={i === heroSlide}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === heroSlide ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
                      }`}
                  />
                ))}
              </div>

              <div className="glass absolute -bottom-4 -left-4 flex items-center gap-2 rounded-full px-4 py-2 shadow-soft">
                <Zap size={14} className="text-primary-600 dark:text-primary-300" />
                <span className="text-xs font-semibold text-slate-800 dark:text-white">
                  {liveReaders !== null ? `${formatCompactNumber(liveReaders)} reading now` : 'Live now'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <AuthorInfoModal
        open={authorInfoOpen}
        onClose={() => setAuthorInfoOpen(false)}
        onContinue={() => { setAuthorInfoOpen(false); navigate('/publish'); }}
        royaltyPercent={royaltyPercent}
      />
    </section>
  );
}
